import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ArrowLeft, CalendarClock, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { getCurrencySymbol } from "@/utils/currency";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { statusBadgeClass } from "@/lib/status";

export function BookingDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId } = useCurrentStore();

  const { data: booking, isLoading } = useQuery({
    queryKey: ["booking", id, storeId],
    queryFn: async () => {
      const { data } = await api.get(`/api/bookings/${id}`, {
        headers: { "Store-ID": storeId },
      });
      return data;
    },
    enabled: !!id && !!storeId,
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ status, note }: { status: string; note?: string }) => {
      return api.patch(
        `/api/bookings/${id}/status`,
        { status, note, notifyCustomer: true },
        { headers: { "Store-ID": storeId } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking", id] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      toast.success("Booking status updated");
    },
    onError: () => {
      toast.error("Failed to update booking status");
    },
  });

  const updatePaymentMutation = useMutation({
    mutationFn: async ({
      status,
      transactionId,
    }: {
      status: string;
      transactionId?: string;
    }) => {
      return api.patch(
        `/api/bookings/${id}/payment`,
        { status, transactionId },
        { headers: { "Store-ID": storeId } }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking", id] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      toast.success("Payment status updated");
    },
    onError: () => {
      toast.error("Failed to update payment status");
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6" role="status" aria-label="Loading booking">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-24 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-72 w-full rounded-[1.25rem]" />
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Booking not found</p>
      </div>
    );
  }

  const getStatusColor = statusBadgeClass;
  const getPaymentStatusColor = statusBadgeClass;

  return (
    <div className="space-y-6">
      <DashboardHead pageName={`Booking ${booking.bookingNumber}`} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
        <Button
          variant="outline"
          onClick={() => navigate("/dashboard/bookings")}
          className="h-10 text-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">
              Booking #{booking.bookingNumber}
            </h1>
            <p className="text-sm text-muted-foreground">
              {booking.customer.name} •{" "}
              {format(new Date(booking.bookingDetails.startDate), "PPp")}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className={getStatusColor(booking.status)}>
            {booking.status.replace("_", " ")}
          </Badge>
          <Badge className={getPaymentStatusColor(booking.payment.status)}>
            {booking.payment.status.replace("_", " ")}
          </Badge>
        </div>
      </div>

      <Card>
        <CardContent className="grid gap-4 px-4 py-4 md:grid-cols-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
              <CalendarClock className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Schedule
              </p>
              <p className="text-sm font-semibold">
                {format(new Date(booking.bookingDetails.startDate), "PP")}
                {booking.bookingDetails.startTime && (
                  <span className="text-muted-foreground">
                    {" "}
                    at {booking.bookingDetails.startTime}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
              <CreditCard className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Amount
              </p>
              <p className="text-sm font-semibold">
                {getCurrencySymbol(booking.pricing.currency || "NGN")}
                {booking.pricing.total}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {booking.payment.method.replace("_", " ")}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-start gap-3 md:justify-end">
            <div className="text-left md:text-right">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Slot
              </p>
              <p className="text-sm font-semibold">{booking.slot.snapshot.name}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {booking.slot.snapshot.bookingType.replace("_", " ")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Booking Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Slot</Label>
                <p className="font-medium">{booking.slot.snapshot.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Booking Type</Label>
                <p className="font-medium capitalize">
                  {booking.slot.snapshot.bookingType.replace("_", " ")}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Start Date</Label>
                <p className="font-medium">
                  {format(new Date(booking.bookingDetails.startDate), "PPP")}
                  {booking.bookingDetails.startTime && (
                    <span className="text-muted-foreground">
                      {" "}
                      at {booking.bookingDetails.startTime}
                    </span>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">End Date</Label>
                <p className="font-medium">
                  {format(new Date(booking.bookingDetails.endDate), "PPP")}
                  {booking.bookingDetails.endTime && (
                    <span className="text-muted-foreground">
                      {" "}
                      at {booking.bookingDetails.endTime}
                    </span>
                  )}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Duration</Label>
                <p className="font-medium">{booking.slot.snapshot.duration}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Capacity</Label>
                <p className="font-medium">{booking.slot.snapshot.capacity}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{booking.customer.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{booking.customer.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Phone</Label>
                <p className="font-medium">{booking.customer.phone}</p>
              </div>
              {booking.customer.address && (
                <div>
                  <Label className="text-muted-foreground">Address</Label>
                  <p className="font-medium">{booking.customer.address}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Status & Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Booking Status</Label>
                <Select
                  value={booking.status}
                  onValueChange={(value) =>
                    updateStatusMutation.mutate({ status: value })
                  }
                  disabled={updateStatusMutation.isPending}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Payment Status</Label>
                <Select
                  value={booking.payment.status}
                  onValueChange={(value) =>
                    updatePaymentMutation.mutate({ status: value })
                  }
                  disabled={updatePaymentMutation.isPending}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="text-lg font-bold">
                    {getCurrencySymbol(booking.pricing.currency || "NGN")}
                    {booking.pricing.total}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Payment Method</span>
                  <span className="capitalize">
                    {booking.payment.method.replace("_", " ")}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {booking.timeline?.map((entry: any, index: number) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{entry.status}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(entry.timestamp), "PPp")}
                      </p>
                      {entry.note && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {entry.note}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

